// FallbackUsedBanner.tsx — ★ M2 (FE_SPEC §9.2 + P0-12).
//
// Mounts on Live.tsx 顶部 当 store.fallback_used === true. 颜色按 reason:
//   - 一般 fallback (composer_*) = 黄色 amber (议会继续)
//   - fallback_invalid = 红色 vermilion (与 abort banner 同级 · 议会已 panic)
//
// 不阻塞议会 · 信息透明 · 用户知道当前用的是 static fallback choreography.

import { useTranslation } from 'react-i18next';

export type FallbackReason =
  | 'composer_unavailable'
  | 'composer_json_invalid'
  | 'composer_invariant_violation'
  | 'composer_timeout'
  | 'fallback_invalid'
  | string; // forward-compat

interface FallbackUsedBannerProps {
  reason: FallbackReason | null;
}

export default function FallbackUsedBanner({ reason }: FallbackUsedBannerProps) {
  const { t } = useTranslation();
  if (!reason) return null;

  const isCritical = reason === 'fallback_invalid';
  const palette = isCritical
    ? 'border-vermilion bg-vermilion-50 text-vermilion-700'
    : 'border-amber-500 bg-amber-50 text-amber-800';

  const reasonText = t(`live.fallback.reason.${reason}`, {
    defaultValue:
      reason === 'composer_unavailable'
        ? '编排引擎暂不可用 · 已切到静态议程'
        : reason === 'composer_json_invalid'
          ? '编排返回数据异常 · 已切到静态议程'
          : reason === 'composer_invariant_violation'
            ? '编排不变量违反 · 已切到静态议程'
            : reason === 'composer_timeout'
              ? '编排超时 · 已切到静态议程'
              : reason === 'fallback_invalid'
                ? '议会编排引擎严重异常 · 议会已自动中止 · 系统已全额退款'
                : reason,
  });

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="fallback-used-banner"
      data-reason={reason}
      className={`mb-4 px-4 py-3 border rounded-md text-sm ${palette}`}
    >
      <span className="font-mono text-[10px] tracking-meta uppercase mr-2">
        {t('live.fallback.used', { defaultValue: 'fallback' })}
      </span>
      <span>{reasonText}</span>
    </div>
  );
}
